import {
  GetPoolsQuery,
  GetRemittanceHistoriesByClientAndRoleQuery,
  PoolsForBuyerQuery,
  RemittanceStatus,
} from "@/services/graphql/reactQuery";
import { SortDirection } from "@ncino/pax-ui";
import { ReactNode } from "react";
import z from "zod";

// listing is in pax.index.graphql but absent from the generated type until codegen is re-run
export type SoldPool = GetPoolsQuery["pools"][number] & {
  listing?: { poolId: number; price: number } | null;
};

export type PurchasedPool = PoolsForBuyerQuery["poolParticipants"][number];

export type RemittanceHistory =
  GetRemittanceHistoriesByClientAndRoleQuery["remittanceHistories"][number];

export interface SharedPoolSchema {
  poolId: number;
  poolName: string;
  loanType: string;
  numLoans: number;
  price: number;
  reportingPeriod: string;
  status: RemittanceStatus;
  actions: ManagedPoolAction[];
  origin: PoolOrigination;
}

export interface PurchasePoolEntry extends SharedPoolSchema {
  seller: string;
  purchaseDate: string;
}

export interface SoldPoolEntry extends SharedPoolSchema {
  buyer: string;
  sellDate: string;
}

export type LoanPoolItem = {
  name: string;
  id: number;
  origin: PoolOrigination;
  tabId: string;
  count: number;
  reportingPeriod: string;
  status: RemittanceStatus;
};

export type ManagedPoolActionName =
  | "Review Remittance"
  | "Create Remittance"
  | "Review Data Quality"
  | "Complete Reconciliation"
  | "View Pool Details"
  | "Load Data";
export interface ManagedPoolAction {
  name: ManagedPoolActionName;
  icon: JSX.Element;
  onClick: (actionPayload: any) => void;
}
export interface RowActionMap {
  openModal: (loanPool?: LoanPoolItem) => void;
  openRemittanceModal: (loanPool: LoanPoolItem) => void;
  openTab: (loanPool: LoanPoolItem, active?: boolean) => void;
  openLoadTransactionDataModal: (loanPool: LoanPoolItem) => void;
}

export const PoolOriginationSchema = z.enum(["Sold", "Purchased"]);
export type PoolOrigination = z.infer<typeof PoolOriginationSchema>;

export enum AnalyticsReport {
  OVERVIEW = "overview",
  CONCENTRATION = "concentration",
  CHARGE_OFF = "chargeOff",
  DELINQUENCY = "delinquency",
}

export interface BasePoolDetailProps {
  portfolioId: number;
  poolId: string;
  dataDate: string;
  loanIdNumbers: string[];
}

export const ManageAnchorTabs = z.enum(["sold", "purchased"]);
export type ManageAnchorTabKeys = z.infer<typeof ManageAnchorTabs>;

export const ManageDetailTabs = z.enum(["analytics", "loanList"]);
export type ManageDetailTabKeys = z.infer<typeof ManageDetailTabs>;

export const ManageAnalyticsReports = z.enum([
  "overview",
  "chargeOff",
  "concentration",
  "delinquency",
]);
export type ManageAnalyticsReportKeys = z.infer<typeof ManageAnalyticsReports>;

type CellAlignment = "inherit" | "left" | "center" | "right" | "justify";

interface Header<T, K extends keyof T> {
  displayName: string;
  columnKey: K;

  align?: CellAlignment;
  sortable?: boolean;
  converter?: (v: T[K], row: T) => ReactNode;
}

export type TableHeaders<T> = {
  [K in keyof T]: Header<T, K>;
}[keyof T][];

export type PoolTableConfig<T> = {
  currentPage: number;
  rowsPerPage: number;
  sort: SortConfig<T>;
};

export type SortConfig<T> = {
  column: keyof T;
  direction: SortDirection;
};
